import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, RefreshCw, Clock, Eye, Monitor, Globe, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDuration(seconds) {
  if (!seconds || seconds < 1) return '< 1s';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('fr-CA', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function AdminAnalytics() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async (pw) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/tracking/dashboard?password=${encodeURIComponent(pw)}`);
      if (res.status === 401) {
        setError('Mot de passe invalide');
        setAuthenticated(false);
        return;
      }
      const data = await res.json();
      setRows(data.rows || []);
      setAuthenticated(true);
    } catch {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    fetchData(password);
  };

  const handleRefresh = () => fetchData(password);

  const handleClearData = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/tracking/clear?password=${encodeURIComponent(password)}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setRows([]);
        setShowDeleteConfirm(false);
      } else {
        setError('Erreur lors de la suppression');
      }
    } catch {
      setError('Erreur de connexion');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteEntry = async (row, index) => {
    try {
      const res = await fetch(`${API}/api/tracking/delete-entry?password=${encodeURIComponent(password)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ip: row.ip,
          page: row.page,
          user_agent: row.user_agent
        })
      });
      if (res.ok) {
        setRows(rows.filter((_, i) => i !== index));
      } else {
        setError('Erreur lors de la suppression');
      }
    } catch {
      setError('Erreur de connexion');
    }
  };

  // Auto-refresh every 30s
  useEffect(() => {
    if (!authenticated) return;
    const interval = setInterval(() => fetchData(password), 30000);
    return () => clearInterval(interval);
  }, [authenticated, password, fetchData]);

  // Aggregate stats
  const uniqueIPs = [...new Set(rows.map(r => r.ip))].length;
  const totalVisits = rows.reduce((sum, r) => sum + (r.visits || 0), 0);
  const totalTime = rows.reduce((sum, r) => sum + (r.total_duration || 0), 0);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center" data-testid="admin-login">
        <form onSubmit={handleLogin} className="bg-gray-900 border border-gray-800 rounded-xl p-8 w-full max-w-sm">
          <h1 className="text-xl font-bold text-white mb-1">Analytics Admin</h1>
          <p className="text-sm text-gray-500 mb-6">Acces restreint</p>
          {error && <p className="text-red-400 text-sm mb-4" data-testid="admin-error">{error}</p>}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm mb-4 focus:outline-none focus:border-lime-500"
            autoFocus
            data-testid="admin-password-input"
          />
          <button
            type="submit"
            className="w-full py-2.5 bg-white text-gray-900 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors"
            data-testid="admin-login-btn"
          >
            Acceder
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-200">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gray-950/95 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm" data-testid="admin-back-btn">
              <ArrowLeft className="h-4 w-4" />
              Retour
            </a>
            <div className="h-5 w-px bg-gray-700" />
            <h1 className="text-sm font-semibold text-white">Tracking Analytics</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 rounded-lg text-xs"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Rafraichir
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-900/50 rounded-lg text-xs"
              data-testid="admin-clear-btn"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Effacer
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="stat-unique-visitors">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
              <Globe className="h-3.5 w-3.5" />
              Visiteurs uniques
            </div>
            <p className="text-2xl font-bold text-white">{uniqueIPs}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="stat-total-visits">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
              <Eye className="h-3.5 w-3.5" />
              Total visites
            </div>
            <p className="text-2xl font-bold text-white">{totalVisits}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="stat-total-time">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
              <Clock className="h-3.5 w-3.5" />
              Temps total
            </div>
            <p className="text-2xl font-bold text-white">{formatDuration(totalTime)}</p>
          </div>
        </div>

        {/* Data table */}
        {rows.length === 0 ? (
          <div className="text-center py-20 text-gray-600" data-testid="no-data">
            <Monitor className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Aucune donnee de tracking pour le moment.</p>
            <p className="text-xs mt-1">Les visites apparaitront ici automatiquement.</p>
          </div>
        ) : (
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden" data-testid="tracking-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3">IP</th>
                  <th className="text-left px-4 py-3">Appareil</th>
                  <th className="text-left px-4 py-3">Page</th>
                  <th className="text-center px-4 py-3">Visites</th>
                  <th className="text-center px-4 py-3">Temps</th>
                  <th className="text-center px-4 py-3">Scroll</th>
                  <th className="text-left px-4 py-3">Derniere visite</th>
                  <th className="text-center px-4 py-3">Detail</th>
                  <th className="text-center px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <React.Fragment key={i}>
                    <tr className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-gray-400">{row.ip}</td>
                      <td className="px-4 py-3 text-xs text-gray-300">{row.device}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-gray-800 rounded text-xs text-lime-400 font-medium">{row.page}</span>
                      </td>
                      <td className="px-4 py-3 text-center font-medium text-white">{row.visits}</td>
                      <td className="px-4 py-3 text-center text-gray-300">{formatDuration(row.total_duration)}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                            <div className="h-full bg-lime-500 rounded-full" style={{ width: `${row.max_scroll || 0}%` }} />
                          </div>
                          <span className="text-xs text-gray-500">{row.max_scroll || 0}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">{formatDate(row.last_visit)}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                          className="text-gray-500 hover:text-white transition-colors"
                          data-testid={`expand-row-${i}`}
                        >
                          {expandedRow === i ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDeleteEntry(row, i)}
                          className="text-gray-500 hover:text-red-400 transition-colors p-1"
                          title="Supprimer cette entree"
                          data-testid={`delete-row-${i}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                    {expandedRow === i && (
                      <tr className="bg-gray-800/20">
                        <td colSpan={9} className="px-6 py-4">
                          <div className="grid grid-cols-3 gap-4 text-xs">
                            <div>
                              <p className="text-gray-500 mb-1">Ecran</p>
                              <p className="text-gray-300">{row.screen_width}x{row.screen_height}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 mb-1">Sessions</p>
                              <p className="text-gray-300">{row.sessions}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 mb-1">Sections atteintes</p>
                              <div className="flex flex-wrap gap-1">
                                {row.sections_reached && row.sections_reached.length > 0
                                  ? row.sections_reached.map((s, j) => (
                                      <span key={j} className="px-1.5 py-0.5 bg-gray-800 rounded text-gray-400">{s}</span>
                                    ))
                                  : <span className="text-gray-600">-</span>
                                }
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="delete-modal">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-900/30 rounded-full flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Effacer toutes les donnees</h3>
                <p className="text-sm text-gray-500">Cette action est irreversible</p>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-6">
              Vous etes sur le point de supprimer <strong className="text-white">{rows.length} enregistrements</strong> de tracking. 
              Cette action ne peut pas etre annulee.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 rounded-lg text-sm font-medium"
                data-testid="delete-cancel-btn"
              >
                Annuler
              </button>
              <button
                onClick={handleClearData}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                data-testid="delete-confirm-btn"
              >
                {deleting ? 'Suppression...' : 'Confirmer la suppression'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
